import { $createListItemNode, $createListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode } from "@lexical/rich-text";
import { PortalType } from "contexts/portal.context";
import type { ParagraphNode } from "lexical";
import { $createParagraphNode, $createTextNode, $getRoot } from "lexical";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EPortalType } from "store/types";

interface IDefaultContentPlugin {
    /**
     * Show default content on template editor
     * Only show default content when it's a new template
     */
    showDefaultContent: boolean;
}
export const DefaultContentPlugin = ({ showDefaultContent }: IDefaultContentPlugin) => {
    const [editor] = useLexicalComposerContext();
    const [t] = useTranslation();
    const portalType = useContext(PortalType);

    useEffect(() => {
        if (!showDefaultContent) return;
        editor.update(() => {
            const root = $getRoot();
            const isEditorEmpty = !root?.getTextContent()?.length;

            if (isEditorEmpty) {
                const heading = $createHeadingNode("h1");
                heading.append($createTextNode(t("RichEditor.DefaultContent.Welcome")));
                heading.append($createTextNode("Crealo").toggleFormat("bold"));
                (root?.getFirstChild() as ParagraphNode).append(heading);

                const emptyParagraph = $createParagraphNode();
                root.append(emptyParagraph);

                const paragraph = $createParagraphNode();
                paragraph.append($createTextNode(t("RichEditor.DefaultContent.FeatureExample")));
                root.append(paragraph);

                const listNode = $createListNode("bullet");

                const item1 = $createListItemNode();
                item1.append($createTextNode(t("RichEditor.DefaultContent.NewArticle")));
                listNode.append(item1);

                const item2 = $createListItemNode();
                item2.append($createTextNode(t("RichEditor.DefaultContent.InsertConditional")));
                listNode.append(item2);

                const item3 = $createListItemNode();
                item3.append($createTextNode(t("RichEditor.DefaultContent.InsertRepeatable")));
                listNode.append(item3);

                if (portalType === EPortalType.BOOK) {
                    const articleRepeatItem = $createListItemNode();
                    articleRepeatItem.append($createTextNode(t("RichEditor.DefaultContent.InsertRepeatableArticle")));
                    listNode.append(articleRepeatItem);
                }

                const item4 = $createListItemNode();
                item4.append($createTextNode(t("RichEditor.DefaultContent.InsertVariable")));
                item4.append($createTextNode("Crealo").toggleFormat("bold"));
                item4.append($createTextNode(t("RichEditor.DefaultContent.ValidationStep")));
                listNode.append(item4);

                root.append(listNode);
                root.append($createParagraphNode());
            }
        });
    }, [editor, portalType]);

    return null;
};

export default DefaultContentPlugin;
